/**
 * <s-card-scroller> holds a single horizontal row of product cards.
 * Commands (dispatched via commandfor/command on buttons anywhere on the page):
 *   --scroll-prev  scroll one view back
 *   --scroll-next  scroll one view forward
 * Buttons that target this element are disabled at the start/end of the row.
 */
class CardScrollerElement extends HTMLElement {
  #track = null;
  #frame = 0;

  connectedCallback() {
    this.#track = this.querySelector('ul');
    this.addEventListener('command', this.#onCommand);
    this.#track?.addEventListener('scroll', this.#queueUpdate, { passive: true });
    window.addEventListener('resize', this.#queueUpdate, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('command', this.#onCommand);
    this.#track?.removeEventListener('scroll', this.#queueUpdate);
    window.removeEventListener('resize', this.#queueUpdate);
    cancelAnimationFrame(this.#frame);
  }

  #onCommand = (event) => {
    if (!this.#track) return;
    const direction = event.command === '--scroll-next' ? 1 : event.command === '--scroll-prev' ? -1 : 0;
    if (direction === 0) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isRtl = getComputedStyle(this.#track).direction === 'rtl';
    this.#track.scrollBy({
      left: direction * (isRtl ? -1 : 1) * this.#track.clientWidth * 0.9,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  #queueUpdate = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    if (!this.#track || !this.id) return;
    const track = this.#track;
    const scrolled = Math.abs(track.scrollLeft);
    const atStart = scrolled <= 2;
    const atEnd = scrolled + track.clientWidth >= track.scrollWidth - 2;
    for (const button of document.querySelectorAll(`button[commandfor="${CSS.escape(this.id)}"]`)) {
      if (button.getAttribute('command') === '--scroll-prev') button.disabled = atStart;
      if (button.getAttribute('command') === '--scroll-next') button.disabled = atEnd;
    }
  }
}

if (!customElements.get('s-card-scroller')) {
  customElements.define('s-card-scroller', CardScrollerElement);
}
